import * as Cesium from 'cesium';
import { createMeasurementLine } from './cesiumEntityFactory';
const EARTH_RADIUS_M = 6378137;
// Sample N positions around a circle on the globe surface. Uses an
// equirectangular offset which is accurate for radii up to a few km at
// most latitudes — same approach as the KML/GeoJSON export.
export function computeCircleRing(center, radiusMeters, segments = 64) {
    const centerCart = Cesium.Cartographic.fromCartesian(center);
    const lat = centerCart.latitude;
    const lng = centerCart.longitude;
    const positions = [];
    for (let i = 0; i <= segments; i++) {
        const t = (i / segments) * 2 * Math.PI;
        const dLat = (radiusMeters * Math.cos(t)) / EARTH_RADIUS_M;
        const dLng = (radiusMeters * Math.sin(t)) / (EARTH_RADIUS_M * Math.cos(lat));
        positions.push(Cesium.Cartesian3.fromRadians(lng + dLng, lat + dLat));
    }
    return positions;
}
export function createCircleCenterPoint(dataSource, position, color, opacity = 1.0) {
    const baseColor = Cesium.Color.fromCssColorString(color);
    return dataSource.entities.add({
        position,
        point: {
            pixelSize: 12,
            color: baseColor.withAlpha(opacity),
            outlineColor: Cesium.Color.BLACK.withAlpha(opacity),
            outlineWidth: 2,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
    });
}
// Default fill opacity matches the previous hardcoded 0.18 — chosen so
// the fill reads as a subtle wash that doesn't compete with the outline.
export const DEFAULT_CIRCLE_FILL_OPACITY = 0.18;
// Fill-only ellipse (no outline). Boundary is drawn separately as a
// polyline (see createCircleOutline) so the line-width control actually
// works — Cesium's ellipse.outlineWidth is clamped to 1px by most
// browsers' WebGL stack and ignores any value above that.
export function createCircleEntity(dataSource, center, radiusMeters, color, fillOpacity = DEFAULT_CIRCLE_FILL_OPACITY) {
    const baseColor = Cesium.Color.fromCssColorString(color);
    return dataSource.entities.add({
        position: center,
        ellipse: {
            semiMajorAxis: radiusMeters,
            semiMinorAxis: radiusMeters,
            material: new Cesium.ColorMaterialProperty(baseColor.withAlpha(fillOpacity)),
            outline: false,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
        },
    });
}
// Polyline tracing the circle perimeter — Cesium polylines honor width
// reliably across browsers (via screen-space billboards) while
// ellipse.outlineWidth does not. Use this for the visible boundary
// post-commit.
export function createCircleOutline(dataSource, center, radiusMeters, color, width = 3, viewer) {
    const positions = computeCircleRing(center, radiusMeters);
    return createMeasurementLine(dataSource, positions, color, 1.0, width, viewer);
}
export function createCircleLabel(dataSource, position, text, opacity = 1.0) {
    return dataSource.entities.add({
        position: position,
        label: {
            text: text,
            font: '14px sans-serif',
            fillColor: Cesium.Color.WHITE.withAlpha(opacity),
            outlineColor: Cesium.Color.BLACK.withAlpha(opacity),
            outlineWidth: 2,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            pixelOffset: new Cesium.Cartesian2(0, -16),
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
    });
}
